import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingBag, 
  Trash2, 
  ArrowLeft, 
  MessageCircle, 
  Share2, 
  Copy, 
  Check, 
  CheckCircle2,
  ExternalLink, 
  Sparkles, 
  Clock, 
  PhoneCall, 
  X,
  Maximize2,
  Eye
} from 'lucide-react';
import CollectionHeader from '../components/CollectionHeader';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { trackEvent } from '../lib/analytics';
import { toast } from 'sonner';

const CartPage = () => {
  const { cart, cartCount, removeFromCart, clearCart, getWhatsAppUrl, getWhatsAppMessageText, phoneNumber } = useCart();
  const [copied, setCopied] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Track cart page view in GA and PostHog
  useEffect(() => {
    trackEvent('cart_page_view', {
      items_count: cartCount,
      item_ids: cart.map((i) => i.id).join(','),
      item_codes: cart.map((i) => i.title).join(','),
    });
  }, [cartCount, cart]);

  const handleWhatsAppClick = () => {
    trackEvent('whatsapp_inquiry_click', {
      items_count: cartCount,
      item_ids: cart.map((i) => i.id).join(','),
      item_codes: cart.map((i) => i.title).join(','),
    });
  };

  const handleCopyText = async () => {
    try {
      const text = getWhatsAppMessageText();
      await navigator.clipboard.writeText(text);
      setCopied(true);
      trackEvent('copy_cart_message_click', { items_count: cartCount });
      toast.success('Inquiry details copied to clipboard! 📋', {
        description: 'You can paste it directly into WhatsApp or SMS.',
      });
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error('Could not copy to clipboard');
    }
  };

  const handleNativeShare = async () => {
    const text = getWhatsAppMessageText();
    trackEvent('share_cart_click', { items_count: cartCount });
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Selected Jewellery Items - Makeup by Khushi',
          text: text,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    } else {
      handleCopyText();
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      <CollectionHeader />

      <main className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
        {/* Top Breadcrumb & Title */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-stone-200">
          <div>
            <Link
              to="/my-collection"
              className="inline-flex items-center text-xs sm:text-sm font-medium text-stone-600 hover:text-rose-600 transition-colors mb-2"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" />
              Back to Collection Catalog
            </Link>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-stone-900 flex items-center">
              <span>My Inquiry Cart</span>
              {cartCount > 0 && (
                <span className="ml-3 text-xs sm:text-sm font-sans font-semibold bg-rose-100 text-rose-800 px-2.5 py-0.5 rounded-full">
                  {cartCount} item{cartCount > 1 ? 's' : ''}
                </span>
              )}
            </h1>
          </div>

          {cartCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearCart}
              className="text-stone-500 hover:text-rose-600 hover:bg-rose-50 border-stone-300 text-xs"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Clear All Items
            </Button>
          )}
        </div>

        {/* Empty State */}
        {cartCount === 0 ? (
          <div className="bg-white rounded-2xl border border-rose-100 p-8 sm:p-16 text-center max-w-2xl mx-auto shadow-xs my-8">
            <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-5 text-rose-500">
              <ShoppingBag className="w-10 h-10 stroke-[1.5]" />
            </div>
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-stone-800 mb-2">
              Your inquiry cart is empty
            </h2>
            <p className="text-stone-600 text-sm sm:text-base max-w-md mx-auto mb-6">
              Browse our handcrafted collection, select the jewellery and accessories you like, and send them to Khushi on WhatsApp for pricing and orders.
            </p>
            <Link to="/my-collection">
              <Button className="bg-rose-600 hover:bg-rose-700 text-white shadow-md text-sm sm:text-base px-6 py-2.5">
                <Sparkles className="w-4 h-4 mr-2" />
                Browse Jewellery Collection
              </Button>
            </Link>
          </div>
        ) : (
          /* Cart Layout: 2 Columns on desktop */
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left: Cart Items List */}
            <div className="lg:col-span-7 space-y-4">
              <div className="space-y-3">
                {cart.map((item, index) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl border border-stone-200/90 p-3 sm:p-4 shadow-xs hover:shadow-md transition-shadow flex items-center space-x-3 sm:space-x-4"
                  >
                    {/* Thumbnail with Click to Expand */}
                    <div
                      className="relative w-20 h-24 sm:w-24 sm:h-28 bg-stone-100 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer group"
                      onClick={() => {
                        trackEvent('cart_item_preview_click', { item_id: item.id, item_code: item.title });
                        setSelectedPhoto(item);
                      }}
                    >
                      <img
                        src={`https://lh3.googleusercontent.com/d/${item.id}=w400`}
                        alt={item.name || `Item #${index + 1}`}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://lh3.googleusercontent.com/d/${item.id}`;
                        }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center transition-all">
                        <Maximize2 className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded">
                          {item.title || `Item #${index + 1}`}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm font-semibold text-stone-800 truncate mt-1">
                        Handcrafted Jewellery Design
                      </p>
                      <p className="text-[11px] text-stone-500 truncate mt-0.5">
                        Code: {item.name || item.id.substring(0, 12)}
                      </p>

                      <div className="mt-2 flex items-center space-x-3 text-xs">
                        <button
                          onClick={() => {
                            trackEvent('cart_item_preview_click', { item_id: item.id, item_code: item.title });
                            setSelectedPhoto(item);
                          }}
                          className="text-stone-500 hover:text-rose-600 underline font-medium flex items-center"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View photo
                        </button>
                        <a
                          href={`https://lh3.googleusercontent.com/d/${item.id}=w1600`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-stone-400 hover:text-stone-600 flex items-center"
                          title="Open original high-res image"
                        >
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </a>
                      </div>
                    </div>

                    {/* Remove Action */}
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="p-2 text-stone-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                      title="Remove from cart"
                    >
                      <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add more items link */}
              <div className="pt-2">
                <Link to="/my-collection">
                  <Button variant="outline" className="w-full border-dashed border-stone-300 text-stone-600 hover:bg-stone-50 text-xs sm:text-sm">
                    + Add More Pieces from Collection
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: WhatsApp Inquiry & Order Summary */}
            <div className="lg:col-span-5">
              <div className="sticky top-20 bg-white rounded-2xl border border-rose-200/90 shadow-lg p-5 sm:p-6 space-y-5">
                {/* Header */}
                <div className="flex items-start space-x-3 pb-3 border-b border-stone-100">
                  <div className="p-2.5 bg-[#25D366]/15 rounded-xl text-[#25D366] flex-shrink-0">
                    <MessageCircle className="w-6 h-6 fill-[#25D366]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-serif font-bold text-stone-900">
                      Send Inquiry on WhatsApp
                    </h2>
                    <p className="text-xs text-stone-500 mt-0.5">
                      Send your selected items directly to Khushi
                    </p>
                  </div>
                </div>

                {/* Clear Instruction Box: Explains exactly what happens */}
                <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-4 space-y-2">
                  <div className="flex items-center space-x-2 text-emerald-800 font-semibold text-xs sm:text-sm">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span>How WhatsApp Inquiry Works:</span>
                  </div>
                  <ul className="text-xs text-stone-700 space-y-1.5 pl-5 list-disc leading-relaxed">
                    <li>
                      Clicking the button below <strong>automatically opens WhatsApp</strong> on your phone or laptop.
                    </li>
                    <li>
                      All your <strong>{cartCount} selected items & photo preview links</strong> will be pre-filled in the message ready to send.
                    </li>
                    <li>
                      Khushi will reply directly with <strong>prices, customization options & delivery details</strong>.
                    </li>
                  </ul>
                </div>

                {/* Summary numbers */}
                <div className="space-y-2 text-xs sm:text-sm text-stone-600">
                  <div className="flex justify-between py-1 border-b border-stone-100">
                    <span>Selected Items:</span>
                    <span className="font-bold text-stone-900">{cartCount} piece{cartCount > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-stone-100">
                    <span>Pricing & Customization:</span>
                    <span className="font-semibold text-rose-700">Discussed on WhatsApp</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span>Delivery Locations:</span>
                    <span className="font-medium text-stone-700">Pune, Ahmedabad & All India</span>
                  </div>
                </div>

                {/* Big WhatsApp CTA Button */}
                <div className="space-y-2 pt-1">
                  <a
                    href={getWhatsAppUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleWhatsAppClick}
                    className="block w-full"
                  >
                    <Button
                      size="lg"
                      className="w-full bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold py-4 shadow-lg hover:shadow-xl transition-all duration-200 text-sm sm:text-base flex items-center justify-center space-x-2 text-center"
                    >
                      <MessageCircle className="w-5 h-5 fill-white flex-shrink-0" />
                      <span>Send My {cartCount} Items on WhatsApp</span>
                    </Button>
                  </a>
                  <p className="text-[11px] text-center text-stone-500 font-medium pt-1">
                    📲 Opens WhatsApp with your {cartCount} chosen photo links attached for Khushi to review
                  </p>

                  {/* Secondary Share / Copy Actions */}
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyText}
                      className="border-stone-200 text-stone-700 hover:bg-stone-50 text-xs"
                    >
                      {copied ? (
                        <>
                          <Check className="w-3.5 h-3.5 mr-1 text-emerald-600" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5 mr-1" />
                          <span>Copy Message</span>
                        </>
                      )}
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNativeShare}
                      className="border-stone-200 text-stone-700 hover:bg-stone-50 text-xs"
                    >
                      <Share2 className="w-3.5 h-3.5 mr-1" />
                      <span>Share Cart</span>
                    </Button>
                  </div>

                  {/* Expandable Message Preview Box */}
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => setShowPreview(!showPreview)}
                      className="text-xs text-rose-600 hover:text-rose-800 font-medium flex items-center justify-center w-full transition-colors"
                    >
                      {showPreview ? 'Hide Message Preview ▲' : 'Preview Message That Will Be Sent ▼'}
                    </button>

                    {showPreview && (
                      <div className="mt-2.5 p-3 bg-stone-100 rounded-lg border border-stone-200 font-mono text-[11px] text-stone-800 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                        {getWhatsAppMessageText()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Direct Phone / Contact Strip */}
                <div className="pt-3 border-t border-stone-100 text-center">
                  <p className="text-[11px] text-stone-500">
                    Prefer direct calling? Contact Khushi at:
                  </p>
                  <a
                    href={`tel:+${phoneNumber}`}
                    onClick={() => trackEvent('direct_phone_click', { phone: phoneNumber })}
                    className="inline-flex items-center text-xs font-semibold text-rose-700 hover:text-rose-900 mt-1"
                  >
                    <PhoneCall className="w-3.5 h-3.5 mr-1" />
                    +91 90993 02024
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Lightbox Modal for cart thumbnails */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 p-2 text-white bg-white/20 hover:bg-white/30 rounded-full"
            aria-label="Close photo"
          >
            <X className="w-6 h-6" />
          </button>
          <div
            className="max-w-3xl max-h-[85vh] p-2 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`https://lh3.googleusercontent.com/d/${selectedPhoto.id}=w1600`}
              alt={selectedPhoto.name || 'Jewellery piece'}
              className="max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl"
            />
            <p className="text-white text-sm mt-3 font-medium">
              {selectedPhoto.title || selectedPhoto.name}
            </p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CartPage;
